// PersonHoverCard — Radix Popover content that surfaces a node's metadata
// (name, role, department, mail, external flag). Rows whose underlying field
// is null/empty are hidden so the panel never displays "undefined".
//
// The component owns one piece of state: Radix's `open` (managed via
// `Popover.Root`'s `open` / `onOpenChange` props). Everything else is derived
// from the supplied node.

import { useState } from 'react';
import type { ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import type { IOrgGraphNode } from '@skystone/shared';
import { cn } from '@/lib/utils';

interface PersonHoverCardProps {
  /** The node data. */
  node: IOrgGraphNode;
  /** Resolved department label (US6 passes this — we don't look up IDs here). */
  departmentLabel?: string | null;
  /** The trigger element (the PersonNode `<g>`, an avatar, etc.). */
  children: ReactNode;
}

interface RowProps {
  /** Field label (e.g. "Title"). */
  label: string;
  /** Field value — when null/empty the entire row is omitted. */
  value: string | null | undefined;
  /** Optional class override for the value cell (e.g. monospace for mail). */
  valueClassName?: string;
}

function MetaRow({ label, value, valueClassName }: Readonly<RowProps>) {
  if (value === null || value === undefined || value.trim() === '') return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={cn('text-sm text-foreground', valueClassName)}>{value}</span>
    </div>
  );
}

export function PersonHoverCard({ node, departmentLabel, children }: Readonly<PersonHoverCardProps>) {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="center"
          sideOffset={8}
          collisionPadding={8}
          className="z-50 w-[280px] rounded-lg border border-border bg-card p-4 text-left shadow-lg animate-in fade-in-0 zoom-in-95"
          data-testid="person-hover-card"
          aria-label={`Details for ${node.displayName}`}
        >
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <p className="text-base font-semibold text-foreground">{node.displayName}</p>
              {node.isExternal && (
                <span
                  className="rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-warning"
                  data-testid="person-hover-card-external"
                >
                  External
                </span>
              )}
            </div>
            <MetaRow label="Title" value={node.jobTitle} />
            <MetaRow label="Department" value={departmentLabel ?? null} />
            <MetaRow label="Mail" value={node.mail} valueClassName="font-mono text-xs" />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
